import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Upload, Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Smooth easing
const smoothEase = [0.4, 0, 0.2, 1];

const IntakePage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    businessName: '',
    category: '',
    formula: '',
    upgrades: [],
    eventDate: '',
    notes: '',
    files: []
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  const upgradeOptions = [
    { id: 'fast-turnaround', label: 'Fast Turnaround' },
    { id: 'motion-graphics', label: 'Motion Graphics' },
    { id: 'bundle', label: 'Bundle' }
  ];
  
  const categoryOptions = [
    { value: '', label: 'Select category...' },
    { value: 'music-artist', label: 'Music Artist' },
    { value: 'small-business', label: 'Small Business' },
    { value: 'events-promotions', label: 'Events & Promotions' }
  ];
  
  const formulaOptions = [
    { value: '', label: 'Select formula...' },
    { value: 'formula-01', label: 'Formula 01' },
    { value: 'formula-02', label: 'Formula 02' },
    { value: 'formula-03', label: 'Formula 03' }
  ];
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.category) {
      newErrors.category = 'Please select what you purchased';
    }
    
    if (!formData.formula) {
      newErrors.formula = 'Please select the formula purchased';
    }
    
    if (!formData.notes.trim()) {
      newErrors.notes = 'Please provide deliverable details';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleUpgradeToggle = (upgradeId) => {
    setFormData(prev => ({
      ...prev,
      upgrades: prev.upgrades.includes(upgradeId)
        ? prev.upgrades.filter(id => id !== upgradeId)
        : [...prev.upgrades, upgradeId]
    }));
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({ ...prev, files }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create form data for file upload
      const submitData = new FormData();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('businessName', formData.businessName);
      submitData.append('category', formData.category);
      submitData.append('formula', formData.formula);
      submitData.append('upgrades', JSON.stringify(formData.upgrades));
      submitData.append('eventDate', formData.eventDate);
      submitData.append('notes', formData.notes);
      
      // Append files
      formData.files.forEach((file) => {
        submitData.append('files', file);
      });
      
      const response = await fetch(`${BACKEND_URL}/api/intake`, {
        method: 'POST',
        body: submitData
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: smoothEase }}
          className="text-center max-w-md"
        >
          <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-8 h-8 text-background" />
          </div>
          <h1 className="font-display text-4xl text-foreground mb-4">INTAKE RECEIVED</h1>
          <p className="font-body text-muted-foreground mb-8">
            We'll review and follow up shortly.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 font-body text-sm text-accent hover:text-foreground transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Noise overlay */}
      <div className="noise-overlay" />
      
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-16 md:py-24">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: smoothEase }}
          className="mb-12"
        >
          <Link 
            to="/"
            className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </motion.div>
        
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: smoothEase }}
          className="mb-10"
        >
          <img 
            src="/frmla-logo.png" 
            alt="FRMLA Creative" 
            className="w-[180px] md:w-[220px] h-auto"
          />
        </motion.div>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: smoothEase }}
          className="mb-12"
        >
          <p className="font-body text-sm tracking-[0.3em] text-accent/80 uppercase mb-4">
            PROJECT INTAKE
          </p>
          <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">
            LET'S GET STARTED
          </h1>
          <p className="font-body text-muted-foreground">
            Fill out the form below so we can begin your project.
          </p>
        </motion.div>
        
        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: smoothEase }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Full Name */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Full Name <span className="text-accent">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              data-testid="intake-fullname-input"
              value={formData.fullName}
              onChange={handleInputChange}
              className={`w-full bg-card border ${errors.fullName ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300`}
              placeholder="Your full name"
            />
            {errors.fullName && (
              <p className="mt-1 font-body text-xs text-red-500">{errors.fullName}</p>
            )}
          </div>
          
          {/* Email */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Email <span className="text-accent">*</span>
            </label>
            <input
              type="email"
              name="email"
              data-testid="intake-email-input"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full bg-card border ${errors.email ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300`}
              placeholder="your@email.com"
            />
            {errors.email && (
              <p className="mt-1 font-body text-xs text-red-500">{errors.email}</p>
            )}
          </div>
          
          {/* Phone */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Phone <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300"
              placeholder="(555) 123-4567"
            />
          </div>
          
          {/* Business/Artist Name */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Business / Artist Name <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              className="w-full bg-card border border-border px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300"
              placeholder="Your brand or artist name"
            />
          </div>
          
          {/* Divider */}
          <div className="gold-line w-full my-8" />
          
          {/* What did you purchase? */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              What did you purchase? <span className="text-accent">*</span>
            </label>
            <select
              name="category"
              data-testid="intake-category-select"
              value={formData.category}
              onChange={handleInputChange}
              className={`w-full bg-card border ${errors.category ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground focus:outline-none focus:border-accent transition-colors duration-300 appearance-none cursor-pointer`}
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
            >
              {categoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errors.category && (
              <p className="mt-1 font-body text-xs text-red-500">{errors.category}</p>
            )}
          </div>
          
          {/* Formula Purchased */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Formula Purchased <span className="text-accent">*</span>
            </label>
            <select
              name="formula"
              data-testid="intake-formula-select"
              value={formData.formula}
              onChange={handleInputChange}
              className={`w-full bg-card border ${errors.formula ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground focus:outline-none focus:border-accent transition-colors duration-300 appearance-none cursor-pointer`}
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
            >
              {formulaOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errors.formula && (
              <p className="mt-1 font-body text-xs text-red-500">{errors.formula}</p>
            )}
          </div>
          
          {/* Upgrades */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-3">
              Upgrade Selected <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <div className="space-y-2">
              {upgradeOptions.map(upgrade => (
                <button
                  key={upgrade.id}
                  type="button"
                  onClick={() => handleUpgradeToggle(upgrade.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 border transition-colors duration-300 ${
                    formData.upgrades.includes(upgrade.id)
                      ? 'border-accent/60 bg-accent/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                >
                  <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-300 ${
                    formData.upgrades.includes(upgrade.id)
                      ? 'border-accent bg-accent'
                      : 'border-muted-foreground/30'
                  }`}>
                    {formData.upgrades.includes(upgrade.id) && (
                      <Check className="w-3 h-3 text-background" />
                    )}
                  </div>
                  <span className="font-body text-sm text-foreground">{upgrade.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Event Date / Release Date */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Event Date / Release Date <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <input
              type="date"
              name="eventDate"
              value={formData.eventDate}
              onChange={handleInputChange}
              className="w-full bg-card border border-border px-4 py-3 font-body text-foreground focus:outline-none focus:border-accent transition-colors duration-300"
            />
          </div>
          
          {/* Divider */}
          <div className="gold-line w-full my-8" />
          
          {/* Deliverable Details / Notes */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Deliverable Details / Notes <span className="text-accent">*</span>
            </label>
            <textarea
              name="notes"
              data-testid="intake-notes-textarea"
              value={formData.notes}
              onChange={handleInputChange}
              rows={5}
              className={`w-full bg-card border ${errors.notes ? 'border-red-500' : 'border-border'} px-4 py-3 font-body text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300 resize-none`}
              placeholder="Describe your project, vision, colors, references, or any specific requirements..."
            />
            {errors.notes && (
              <p className="mt-1 font-body text-xs text-red-500">{errors.notes}</p>
            )}
          </div>
          
          {/* File Upload */}
          <div>
            <label className="block font-body text-xs tracking-wider text-muted-foreground uppercase mb-2">
              Upload Assets <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <p className="font-body text-xs text-muted-foreground/60 mb-3">
              Logos, images, references, or any files that will help with your project.
            </p>
            <label className="flex items-center justify-center gap-3 w-full border-2 border-dashed border-border hover:border-muted-foreground/40 px-4 py-8 cursor-pointer transition-colors duration-300">
              <Upload className="w-5 h-5 text-muted-foreground" />
              <span className="font-body text-sm text-muted-foreground">
                {formData.files.length > 0 
                  ? `${formData.files.length} file(s) selected`
                  : 'Click to upload files'
                }
              </span>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept="image/*,.pdf,.ai,.psd,.zip"
              />
            </label>
            {formData.files.length > 0 && (
              <div className="mt-2 space-y-1">
                {formData.files.map((file, index) => (
                  <p key={index} className="font-body text-xs text-muted-foreground/60">
                    {file.name}
                  </p>
                ))}
              </div>
            )}
          </div>
          
          {/* Submit Error */}
          {submitError && (
            <p className="font-body text-sm text-red-500 text-center">{submitError}</p>
          )}
          
          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              data-testid="intake-submit-button"
              disabled={isSubmitting}
              className="w-full py-4 font-display text-lg tracking-wider bg-foreground text-background hover:bg-accent transition-colors duration-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  SUBMITTING...
                </>
              ) : (
                'SUBMIT INTAKE'
              )}
            </button>
          </div>
          
          {/* Footer note */}
          <p className="font-body text-xs text-muted-foreground/40 text-center pt-4">
            We typically respond within 24-48 hours.
          </p>
        </motion.form>
      </div>
    </div>
  );
};

export default IntakePage;
